import { createContext, useContext, useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { debounce } from "../helperFunctions";

export const SearchContext = createContext({
  searchTerm: "",
  inputValue: "",
  hideSearch: false,
  handleInputChange: () => {},
  handleSearch: () => {},
  setHideSearch: () => {},
});

export function useSearchContext() {
  return useContext(SearchContext);
}

// Call this inside any page component to hide the topbar search on that page
export function useHideSearch() {
  const { setHideSearch } = useContext(SearchContext);
  useEffect(() => {
    setHideSearch(true);
    return () => setHideSearch(false);
  }, [setHideSearch]);
}

export function SearchProvider({ children }) {
  const [inputValue, setInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [hideSearch, setHideSearch] = useState(false);
  const location = useLocation();

  // Reset on every route change
  useEffect(() => {
    setInputValue("");
    setSearchTerm("");
    setHideSearch(false);
  }, [location.pathname]);

  const debouncedSet = useMemo(
    () => debounce((v) => setSearchTerm(v), 300),
    []
  );

  const isNumeric = (v) => v.length > 0 && /^\d+$/.test(v);

  const handleInputChange = (e) => {
    const v = e.target.value;
    setInputValue(v);
    if (v.length === 0 || isNumeric(v) || v.length >= 3) {
      debouncedSet(v);
    }
  };

  // Immediate commit on Enter / explicit search trigger
  const handleSearch = (v) => {
    if (v.length === 0 || isNumeric(v) || v.length >= 3) {
      setInputValue(v);
      setSearchTerm(v);
    }
  };

  return (
    <SearchContext.Provider
      value={{ searchTerm, inputValue, hideSearch, handleInputChange, handleSearch, setHideSearch }}
    >
      {children}
    </SearchContext.Provider>
  );
}
